/**
 * Hooks local-first de COHORTES y PROGRAMAS (F2-A). Los datos llegan por los
 * streams por rol (my_cohorts_student/teacher, campus/org_admin_scope,
 * academy_org para programas y cohortes abiertas); la escritura pasa por el
 * write-path o los endpoints REST /api/cohorts.
 */
import type { Accessor } from "solid-js";
import {
  cohortQueryKeys,
  type CohortRosterRow,
  type CohortTeacherProfileRow,
  getCohortGroupMembersQuery,
  getCohortGroupsQuery,
  getCohortModuleSettingsQuery,
  getCohortQuery,
  getCohortRosterQuery,
  getCohortsByOrgQuery,
  getMyCohortEnrollmentsQuery,
  getOpenCohortsByCourseQuery,
  getProgramCoursesQuery,
  getProgramQuery,
  getProgramsByOrgQuery,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import { createQuery } from "@tanstack/solid-query";
import type {
  Cohort,
  CohortEnrollment,
  CohortGroup,
  CohortGroupMember,
  CohortModuleSetting,
  Program,
  ProgramCourse,
} from "@space/powersync";
import { fetchOrgCourses, type OrgCourseEntry } from "@/features/academy/api/org-courses";
import { api, createApiQuery } from "@/lib/api";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

export function useCohortsByOrg(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Cohort>({
    queryKey: () => cohortQueryKeys.byOrg(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getCohortsByOrgQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohort(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<Cohort>({
    queryKey: () => cohortQueryKeys.byId(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Cohortes ABIERTAS de un curso en mi org (selección de matrícula). */
export function useOpenCohorts(
  orgId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
) {
  const base = useBase();
  return createPowerSyncQuery<Cohort>({
    queryKey: () => cohortQueryKeys.openByCourse(orgId(), courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const org = orgId();
      const course = courseId();
      return db && org && course ? getOpenCohortsByCourseQuery(db, org, course) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MIS matrículas de cohorte. */
export function useMyCohortEnrollments() {
  const base = useBase();
  return createPowerSyncQuery<CohortEnrollment>({
    queryKey: () => cohortQueryKeys.myEnrollments(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyCohortEnrollmentsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortRoster(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortRosterRow>({
    queryKey: () => cohortQueryKeys.roster(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortRosterQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * Fichas PÚBLICAS de los profesores de una cohorte (1C · portada del aula).
 *
 * E4 · plan 06: por REST (`GET /api/cohorts/:id/teachers`). Antes era un join
 * local que dependía de que `academy_org` replicara TODAS las fichas públicas
 * de la academia a TODO miembro para pintar las dos de un aula. No es
 * identidad: son los datos que el propio profesor decidió publicar; el nombre y
 * el correo siguen viniendo del roster, que no responde a un alumno.
 */
export function useCohortTeacherProfiles(cohortId: Accessor<string | undefined>) {
  return createApiQuery<CohortTeacherProfileRow[], unknown>(
    () => cohortQueryKeys.teacherProfiles(cohortId()),
    async () =>
      api.GET("/api/cohorts/{id}/teachers", {
        params: { path: { id: cohortId() ?? "" } },
      }),
    () => ({ enabled: Boolean(cohortId()), staleTime: OFFLINE_CACHE_STALE_TIME_MS }),
  );
}

// D3 (plan 06 · E3): `useCohortTeachers` se ha BORRADO junto con su consulta
// Drizzle. Era el segundo join de identidad de la cohorte y no lo llamaba
// ninguna pantalla; los profesores de la cohorte vienen hoy en la misma
// respuesta que el roster (`useCohortIdentities`).

export function useCohortGroups(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortGroup>({
    queryKey: () => cohortQueryKeys.groups(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortGroupsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortGroupMembers(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortGroupMember>({
    queryKey: () => cohortQueryKeys.groupMembers(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortGroupMembersQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortModuleSettings(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortModuleSetting>({
    queryKey: () => cohortQueryKeys.moduleSettings(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortModuleSettingsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * Cursos publicados en la org (picker de cohortes/programas) — **camino único
 * por REST**.
 *
 * Plan 06 · E1 (cierra D2), commit restrictivo: el stream `org_courses` ya no
 * existe, así que la rama local y el flag que conmutaba entre las dos fuentes
 * han desaparecido con él. Queda `GET /api/academy/:orgId/courses`, que filtra
 * `deleted_at`, respeta el estado de publicación y exige `ManageAcademics` para
 * los borradores — un alcance que las reglas de sync no expresan.
 *
 * El tipo es el DTO generado desde Rust, sin fila intermedia a mano: el mapeo
 * `{id, title, link_id}` existía solo para imitar la forma de la consulta local
 * que se acaba de borrar.
 */
export function useOrgCourses(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createQuery(() => ({
    queryKey: cohortQueryKeys.orgCoursesRest(orgId()),
    enabled: Boolean(orgId()),
    staleTime: base.staleTime,
    queryFn: (): Promise<OrgCourseEntry[]> => fetchOrgCourses(orgId() ?? ""),
  }));
}

// ─── Programas ───────────────────────────────────────────────────────────────

export function useProgramsByOrg(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Program>({
    queryKey: () => cohortQueryKeys.programs(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getProgramsByOrgQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useProgram(programId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<Program>({
    queryKey: () => cohortQueryKeys.programById(programId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = programId();
      return db && id ? getProgramQuery(db, id) : null;
    },
    enabled: () => Boolean(programId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useProgramCourses(programId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ProgramCourse>({
    queryKey: () => cohortQueryKeys.programCourses(programId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = programId();
      return db && id ? getProgramCoursesQuery(db, id) : null;
    },
    enabled: () => Boolean(programId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
